import type { SinkKind } from './types';

/**
 * Where the archive bytes land.
 *
 * Every strategy defers the "where do you want this?" prompt until the download
 * has finished — nobody should have to answer a file dialog before they've seen
 * a single byte of progress. The difference is where the bytes wait in the
 * meantime. The choice is a capability probe, never UA sniffing, and it also
 * sets how large each part may be (see partPlanner):
 *
 *   opfs          — staged in the Origin Private File System while downloading,
 *                   then streamed to the user's chosen file at the end. On disk
 *                   throughout, so memory stays flat and size is bounded only by
 *                   free space. The default wherever it works.
 *   blob          — accumulate in memory, then save. No staging available, so
 *                   only safe for smaller downloads; the reason mobile parts are
 *                   capped at 400 MB.
 *   serviceworker — last resort for a download too large for memory with no OPFS:
 *                   streams into the browser's own download folder. Saves the
 *                   transfer, but the user gets no say in the destination.
 */

/** What a finished part leaves behind for the UI to act on. */
export interface CompletedPart {
  /**
   * Ask the user where to put this part and write it there. Always deferred to
   * after the download finishes, so the destination prompt never blocks the
   * start of a transfer. Null when the file is already saved (service worker,
   * where the browser owns the download).
   */
  save: (() => Promise<void>) | null;
  /** True when the finished part is held in RAM rather than staged on disk. */
  inMemory: boolean;
  /**
   * Re-open the finished archive, where the platform still lets us reach it.
   *
   * No browser can reveal a file in Finder/Explorer or launch it in another
   * app — that boundary is not crossable from a page. The best available is to
   * hand the bytes back to the browser, which opens its own handling for a
   * .zip. Null when we hold no reference (service-worker sink, where the file
   * belongs to the browser's download manager).
   */
  open: (() => Promise<void>) | null;
}

export interface PartWriter {
  write(chunk: Uint8Array): Promise<void>;
  close(): Promise<CompletedPart>;
  abort(): Promise<void>;
}

/**
 * Unacked chunks allowed in flight to the service worker. client-zip emits
 * chunks well under a megabyte, so this caps worker-side buffering at a few MB
 * while staying deep enough that the pipeline never stalls on ack latency.
 */
const MAX_INFLIGHT_CHUNKS = 8;
/** Give up waiting on acks if the worker stops responding. */
const ACK_TIMEOUT_MS = 30_000;

/** Hand a File/Blob back to the browser so it opens it as a download. */
function openBlobLike(data: Blob, name: string): void {
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Where the archive ended up, when we can name it. Null for sinks that hand the
 * file to the browser's own download location, which a page cannot read.
 */
export interface SinkDestination {
  kind: 'file' | 'folder';
  name: string;
}

export interface DownloadSink {
  readonly kind: SinkKind;
  /** Populated once a picker has been accepted. */
  readonly destination: SinkDestination | null;
  openPart(name: string): Promise<PartWriter>;
  dispose(): void;
}

// ── Capability probes ────────────────────────────────────────────────────────

function hasDirectoryPicker(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function' &&
    window.isSecureContext
  );
}

async function hasStreamingServiceWorker(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;
  // A response body must be constructible from a ReadableStream for the SW to
  // stream at all — Safari historically lacked this.
  try {
    if (typeof ReadableStream !== 'function') return false;
    const probe = new Response(new ReadableStream());
    if (!probe.body) return false;
  } catch {
    return false;
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    return !!reg.active;
  } catch {
    return false;
  }
}

/**
 * Pick the best sink for a download of this size. Size matters because the
 * fallbacks differ sharply: a Blob is fine for 200 MB and fatal for 20 GB.
 */
export async function detectSinkKind(totalBytes: number): Promise<SinkKind> {
  // OPFS first: it is the only option that both defers the destination prompt
  // to the end AND keeps memory flat.
  if (await canStageInOpfs(totalBytes)) return 'opfs';
  // No OPFS. A Blob also defers the prompt, but holds everything in RAM, so it
  // is only safe below the part budget.
  if (totalBytes <= TARGET_BYTES_BLOB) return 'blob';
  // Too big for memory and no staging available — stream to the browser's
  // download folder. The user loses the destination choice, which beats losing
  // the download.
  if (await hasStreamingServiceWorker()) return 'serviceworker';
  return 'blob';
}

/** Kept in step with partPlanner's blob budget. */
const TARGET_BYTES_BLOB = 1 * 1024 ** 3;

// ── OPFS staging (default) ───────────────────────────────────────────────────

interface FileSystemWritableLike {
  write(data: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort?(): Promise<void>;
}
interface FileHandleLike {
  name: string;
  createWritable(): Promise<FileSystemWritableLike>;
  getFile(): Promise<File>;
}
interface DirHandleLike {
  name: string;
  getFileHandle(name: string, opts?: { create?: boolean }): Promise<FileHandleLike>;
  removeEntry(name: string, opts?: { recursive?: boolean }): Promise<void>;
}

/** Scratch directory inside the origin's private filesystem. */
const OPFS_DIR = 'pm-downloads';

function hasFileSystemAccess(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as unknown as { showSaveFilePicker?: unknown }).showSaveFilePicker === 'function' &&
    // The picker requires a secure context; it silently doesn't exist otherwise.
    window.isSecureContext
  );
}

async function opfsRoot(): Promise<DirHandleLike | null> {
  try {
    const storage = navigator.storage as unknown as { getDirectory?: () => Promise<DirHandleLike> };
    if (typeof storage?.getDirectory !== 'function') return null;
    const root = await storage.getDirectory();
    return (await (root as unknown as {
      getDirectoryHandle: (n: string, o?: { create?: boolean }) => Promise<DirHandleLike>;
    }).getDirectoryHandle(OPFS_DIR, { create: true })) as DirHandleLike;
  } catch {
    return null;
  }
}

/**
 * Is there room to stage `bytes` in OPFS? Staging costs disk twice over (the
 * temp copy plus the file the user ends up with), so refuse rather than fill
 * someone's drive and fail at the very end of a long download.
 */
async function opfsHasRoom(bytes: number): Promise<boolean> {
  try {
    const est = await navigator.storage?.estimate?.();
    if (!est || typeof est.quota !== 'number') return true; // unknown → try it
    const free = est.quota - (est.usage ?? 0);
    return free > bytes * 1.1;
  } catch {
    return true;
  }
}

async function canStageInOpfs(totalBytes: number): Promise<boolean> {
  if (!hasFileSystemAccess()) return false; // no picker → no way to save at the end
  const dir = await opfsRoot();
  if (!dir) return false;
  return opfsHasRoom(totalBytes);
}

/**
 * Writes each part into the origin's private filesystem as it downloads, then
 * — once finished — opens the save picker and streams the staged file across.
 *
 * This is what lets the destination prompt come last without giving up
 * streaming: the bytes are on disk the whole time, never in RAM, so a 40 GB
 * album is as cheap to hold as a 40 MB one.
 */
export class OpfsSink implements DownloadSink {
  readonly kind: SinkKind = 'opfs';
  /** Not known until the user picks at the end. */
  destination: SinkDestination | null = null;

  private dir: DirHandleLike | null = null;
  /** Staged, not-yet-saved parts: final name → on-disk handle + staging name. */
  private staged = new Map<string, { handle: FileHandleLike; stagedName: string }>();

  static async prepare(): Promise<OpfsSink | null> {
    const dir = await opfsRoot();
    if (!dir) return null;
    const sink = new OpfsSink();
    sink.dir = dir;
    return sink;
  }

  async openPart(name: string): Promise<PartWriter> {
    if (!this.dir) throw new Error('OpfsSink not prepared');
    const dir = this.dir;
    // Unique staging name: two downloads of the same album must not collide.
    const stagedName = `${Date.now()}-${name}`;
    const handle = await dir.getFileHandle(stagedName, { create: true });
    const writable = await handle.createWritable();
    this.staged.set(name, { handle, stagedName });

    const discard = async () => {
      this.staged.delete(name);
      await dir.removeEntry(stagedName).catch(() => {});
    };

    return {
      write: (chunk) => writable.write(chunk),
      close: async () => {
        await writable.close();
        return {
          inMemory: false,
          // Once saved, the staged copy is deleted — there is nothing left to
          // re-open, and the panel names the destination the user chose instead.
          open: null,
          save: async () => {
            const picker = (window as unknown as {
              showSaveFilePicker: (o: unknown) => Promise<FileHandleLike>;
            }).showSaveFilePicker;
            const dest = await picker({
              suggestedName: name,
              types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }],
            });
            const file = await handle.getFile();
            const out = await dest.createWritable();
            // Disk to disk, streamed — the archive is never materialised in RAM.
            await (file.stream() as unknown as ReadableStream<Uint8Array>).pipeTo(
              out as unknown as WritableStream<Uint8Array>,
            );
            this.destination = { kind: 'file', name: dest.name };
            await discard();
          },
        };
      },
      abort: async () => {
        await writable.abort?.().catch(() => {});
        await discard();
      },
    };
  }

  /**
   * Write every staged part into one folder the user picks once.
   *
   * A four-part download otherwise means four save dialogs at the end. This
   * keeps the single-prompt behaviour the directory picker used to give, just
   * moved to where the user expects it — after the download, not before.
   * Returns false when the picker is unavailable or dismissed, leaving the
   * per-part save buttons as the fallback.
   */
  async saveAllToDirectory(): Promise<boolean> {
    if (!hasDirectoryPicker() || this.staged.size === 0) return false;
    let dest: DirHandleLike;
    try {
      const picker = (window as unknown as {
        showDirectoryPicker: (o?: unknown) => Promise<DirHandleLike>;
      }).showDirectoryPicker;
      dest = await picker({ mode: 'readwrite', startIn: 'downloads' });
    } catch {
      return false; // dismissed
    }

    for (const [name, { handle, stagedName }] of [...this.staged]) {
      const outHandle = await dest.getFileHandle(name, { create: true });
      const out = await outHandle.createWritable();
      const file = await handle.getFile();
      await (file.stream() as unknown as ReadableStream<Uint8Array>).pipeTo(
        out as unknown as WritableStream<Uint8Array>,
      );
      this.staged.delete(name);
      await this.dir?.removeEntry(stagedName).catch(() => {});
    }
    this.destination = { kind: 'folder', name: dest.name };
    return true;
  }

  /** Drop anything the user never saved, so OPFS doesn't accumulate archives. */
  async cleanup(): Promise<void> {
    if (!this.dir) return;
    for (const [name, { stagedName }] of [...this.staged]) {
      await this.dir.removeEntry(stagedName).catch(() => {});
      this.staged.delete(name);
    }
  }

  dispose(): void {
    // Staged files are cleared by cleanup() once the user is done with them —
    // dispose runs as soon as the download ends, while parts may still be
    // waiting to be saved.
  }
}

// ── Service worker streaming ─────────────────────────────────────────────────

/**
 * Streams through the app's existing service worker, which turns the chunks
 * into a real `Content-Disposition: attachment` response. This is the
 * StreamSaver pattern, self-hosted — no third-party MITM iframe, and no second
 * service worker (upload-sw.js already owns scope '/').
 */
export class ServiceWorkerSink implements DownloadSink {
  readonly kind: SinkKind = 'serviceworker';
  /** The browser owns the download location and won't tell us what it is. */
  readonly destination: SinkDestination | null = null;

  static async prepare(): Promise<ServiceWorkerSink | null> {
    return (await hasStreamingServiceWorker()) ? new ServiceWorkerSink() : null;
  }

  async openPart(name: string): Promise<PartWriter> {
    const reg = await navigator.serviceWorker.ready;
    const worker = reg.active;
    if (!worker) throw new Error('No active service worker');

    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const channel = new MessageChannel();

    // Hand the SW one end of the channel; it builds a Response whose body is fed
    // by whatever we post down the port.
    worker.postMessage({ type: 'zip-stream-open', id, filename: name }, [channel.port2]);

    // Navigating a hidden iframe to the SW-served URL starts the download
    // without stealing focus or replacing the page.
    const frame = document.createElement('iframe');
    frame.hidden = true;
    frame.src = `/__zip-stream/${id}`;
    document.body.appendChild(frame);

    // Credit-based flow control. A MessagePort has no backpressure: posting
    // returns immediately, so without this the page would push chunks as fast
    // as the network delivers them and the worker's enqueue() would buffer the
    // entire archive in memory — the exact failure streaming exists to avoid.
    // The worker acks each chunk once enqueued; we stop sending past the cap.
    let inFlight = 0;
    let drain: (() => void) | null = null;
    const releaseDrain = () => {
      const d = drain;
      drain = null;
      d?.();
    };

    let settled = false;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      releaseDrain(); // never leave write() awaiting a port that is closing
      channel.port1.close();
      // Leave the frame briefly so the browser commits the download.
      setTimeout(() => frame.remove(), 2000);
    };

    // Set when the viewer cancels from the browser's own download UI. The next
    // write throws, which unwinds the orchestrator's read loop into its normal
    // abort path — otherwise the download is gone but the panel keeps counting.
    let cancelledByBrowser = false;

    channel.port1.onmessage = (e) => {
      const type = e.data?.type;
      if (type === 'cancelled') {
        cancelledByBrowser = true;
        releaseDrain();
        return;
      }
      if (type !== 'ack') return;
      inFlight = Math.max(0, inFlight - 1);
      if (inFlight < MAX_INFLIGHT_CHUNKS) releaseDrain();
    };

    return {
      write: async (chunk) => {
        if (cancelledByBrowser) throw new Error('Download cancelled in the browser');
        if (inFlight >= MAX_INFLIGHT_CHUNKS && !settled) {
          // Wait for the worker to drain, but never deadlock on a worker that
          // died mid-transfer — fall through and let the stream error instead.
          await new Promise<void>((resolve) => {
            drain = resolve;
            setTimeout(releaseDrain, ACK_TIMEOUT_MS);
          });
        }
        // Transfer the buffer rather than copying it again — these are multi-MB
        // chunks and structured-clone would double peak memory.
        const copy = chunk.slice();
        inFlight++;
        channel.port1.postMessage({ type: 'chunk', payload: copy }, [copy.buffer]);
      },
      close: async () => {
        channel.port1.postMessage({ type: 'close' });
        cleanup();
        // The browser's download manager owns the file now: it is already
        // saved, to a location we are not allowed to know.
        return { save: null, inMemory: false, open: null };
      },
      abort: async () => {
        channel.port1.postMessage({ type: 'abort' });
        cleanup();
      },
    };
  }

  dispose(): void {
    /* nothing retained */
  }
}

// ── Blob fallback ────────────────────────────────────────────────────────────

/** Trigger a browser download for an in-memory Blob. */
export function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Last resort: hold the archive in memory, then save. Safari and iOS have no
 * streaming option, so the part planner keeps parts small here — a multi-GB
 * Blob is what kills the tab.
 */
export class BlobSink implements DownloadSink {
  readonly kind: SinkKind = 'blob';
  readonly destination: SinkDestination | null = null;

  async openPart(name: string): Promise<PartWriter> {
    let chunks: Uint8Array[] | null = [];
    return {
      write: async (chunk) => {
        // Copy: the caller may reuse the underlying buffer.
        chunks!.push(chunk.slice());
      },
      close: async () => {
        const blob = new Blob(chunks as BlobPart[], { type: 'application/zip' });
        chunks = null; // release the chunk array; the Blob owns the bytes now
        return {
          inMemory: true,
          save: async () => saveBlob(blob, name),
          open: async () => openBlobLike(blob, name),
        };
      },
      abort: async () => {
        chunks = null;
      },
    };
  }

  dispose(): void {
    /* nothing retained */
  }
}

/**
 * Build the best available sink, opening any picker the user must see.
 * Call this synchronously inside the click handler — a picker opened after an
 * await chain is rejected as lacking user activation.
 *
 * Falls through to the next-best sink whenever the user dismisses a picker, so
 * cancelling the directory prompt downgrades to a normal download rather than
 * failing the whole operation.
 */
export async function createSink(opts: { totalBytes: number }): Promise<DownloadSink> {
  const kind = await detectSinkKind(opts.totalBytes);

  if (kind === 'opfs') {
    const opfs = await OpfsSink.prepare();
    if (opfs) return opfs;
  }
  if (kind === 'serviceworker') {
    const sw = await ServiceWorkerSink.prepare();
    if (sw) return sw;
  }
  return new BlobSink();
}
